import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { signInWithMagicLink, signInWithPassword, useSession } from '@/lib/auth';
import { ThemeToggle } from '@/components/ThemeToggle';

type Mode = 'password' | 'magic';

export function LoginPage() {
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && session) {
      navigate('/', { replace: true });
    }
  }, [loading, session, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === 'password') {
        await signInWithPassword(email, password);
      } else {
        const redirectTo = `${window.location.origin}/`;
        await signInWithMagicLink(email, redirectTo);
        setInfo('Check je mail voor de inloglink.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inloggen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-bmade-50 px-4 dark:bg-bmade-950">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm rounded-2xl border border-bmade-200 bg-white p-8 shadow-lg shadow-bmade-200/40 dark:border-bmade-800 dark:bg-bmade-900/70 dark:shadow-black/40">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-accent-500/15 p-2">
            <Package className="h-5 w-5 text-accent-600 dark:text-accent-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
              2BMade
            </div>
            <div className="text-lg font-semibold text-bmade-950 dark:text-white">Pakket-tool</div>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
              E-mail
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400"
            />
          </div>

          {mode === 'password' && (
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
                Wachtwoord
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400"
              />
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}
          {info && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
          >
            {busy ? 'Bezig...' : mode === 'password' ? 'Inloggen' : 'Stuur magic link'}
          </button>

          <button
            type="button"
            onClick={() => {
              setMode(mode === 'password' ? 'magic' : 'password');
              setError(null);
              setInfo(null);
            }}
            className="w-full text-center text-xs text-bmade-600 transition-colors hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white"
          >
            {mode === 'password' ? 'Of stuur me een magic link' : 'Of gebruik wachtwoord'}
          </button>
        </form>
      </div>
    </div>
  );
}
